import { useMemo } from 'react';
import { Individual, IndividualCallback } from '../Data';
import { nameAndLifetimeOf } from '../Utils';
import { useTreeState } from '../hooks/useTreeState';
import { TreeNode, SpouseNode } from './TreeNode';
import styles from './FamilyTree.module.css';

interface FamilyTreeProps {
    individual: Individual;
    showDetails: IndividualCallback;
    mode: 'descendants' | 'ancestors';
}

// Collect all individual IDs for expand all functionality
function collectDescendantIds(individual: Individual, ids: Set<number>) {
    ids.add(individual.id);
    for (const family of individual.partnerIn) {
        for (const child of family.children) {
            collectDescendantIds(child, ids);
        }
    }
}

function collectAncestorIds(individual: Individual, ids: Set<number>) {
    ids.add(individual.id);
    for (const parent of individual.parents()) {
        collectAncestorIds(parent, ids);
    }
}

function DescendantTree({
    individual,
    showDetails,
    treeState,
    depth,
}: {
    individual: Individual;
    showDetails: IndividualCallback;
    treeState: ReturnType<typeof useTreeState>;
    depth: number;
}) {
    const familiesWithChildren = individual.partnerIn.filter(f => f.children.length > 0);
    const hasChildren = familiesWithChildren.length > 0;
    const isExpanded = treeState.isExpanded(individual.id);

    return (
        <div className={styles.treeNode}>
            <TreeNode
                individual={individual}
                showDetails={showDetails}
                isExpanded={isExpanded}
                hasChildren={hasChildren}
                onToggle={() => treeState.toggleNode(individual.id)}
                depth={depth}
                isAncestor={false}
            />
            {isExpanded && familiesWithChildren.map((family, familyIndex) => {
                const spouse = family.partners.find(p => p.id !== individual.id);
                return (
                    <div key={family.id} className={styles.familyGroup}>
                        {spouse && (
                            <SpouseNode
                                individual={spouse}
                                showDetails={showDetails}
                                depth={depth + 1}
                            />
                        )}
                        {!spouse && family.children.length > 0 && (
                            <div
                                className={styles.unknownSpouse}
                                style={{ '--depth': depth + 1 } as React.CSSProperties}
                            >
                                <span className={styles.unknownSpouseText}>Unknown spouse</span>
                            </div>
                        )}
                        {family.children.map(child => (
                            <DescendantTree
                                key={child.id}
                                individual={child}
                                showDetails={showDetails}
                                treeState={treeState}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function AncestorTree({
    individual,
    showDetails,
    treeState,
    depth,
}: {
    individual: Individual;
    showDetails: IndividualCallback;
    treeState: ReturnType<typeof useTreeState>;
    depth: number;
}) {
    const parents = individual.parents();
    const hasParents = parents.length > 0;
    const isExpanded = treeState.isExpanded(individual.id);

    return (
        <div className={styles.treeNode}>
            <TreeNode
                individual={individual}
                showDetails={showDetails}
                isExpanded={isExpanded}
                hasChildren={hasParents}
                onToggle={() => treeState.toggleNode(individual.id)}
                depth={depth}
                isAncestor={true}
            />
            {isExpanded && hasParents && (
                <div className={styles.parentsGroup}>
                    {parents.map(parent => (
                        <AncestorTree
                            key={parent.id}
                            individual={parent}
                            showDetails={showDetails}
                            treeState={treeState}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export function FamilyTree({ individual, showDetails, mode }: FamilyTreeProps) {
    const allIds = useMemo(() => {
        const ids = new Set<number>();
        if (mode === 'descendants') {
            collectDescendantIds(individual, ids);
        } else {
            collectAncestorIds(individual, ids);
        }
        return [...ids];
    }, [individual, mode]);

    const treeState = useTreeState(individual.id, allIds);

    const title = mode === 'descendants'
        ? `Descendants of ${nameAndLifetimeOf(individual)}`
        : `Ancestors of ${nameAndLifetimeOf(individual)}`;

    const modeClass = mode === 'descendants' ? styles.descendantsMode : styles.ancestorsMode;

    return (
        <div className={`${styles.container} ${modeClass}`}>
            <div className={styles.header}>
                <h2 className={styles.title}>{title}</h2>
                <div className={styles.controls}>
                    <button
                        className={styles.controlButton}
                        onClick={treeState.expandAll}
                    >
                        Expand All
                    </button>
                    <button
                        className={styles.controlButton}
                        onClick={treeState.collapseAll}
                    >
                        Collapse All
                    </button>
                </div>
            </div>
            <div className={styles.legend}>
                <span className={styles.legendItem}>
                    <span className={styles.legendIcon}>▶</span> Click to expand
                </span>
                <span className={styles.legendItem}>
                    <span className={styles.legendIcon}>▼</span> Click to collapse
                </span>
                <span className={styles.legendItem}>
                    Click name to view details
                </span>
            </div>
            <div className={styles.treeContainer}>
                {mode === 'descendants' ? (
                    <DescendantTree
                        individual={individual}
                        showDetails={showDetails}
                        treeState={treeState}
                        depth={0}
                    />
                ) : (
                    <AncestorTree
                        individual={individual}
                        showDetails={showDetails}
                        treeState={treeState}
                        depth={0}
                    />
                )}
            </div>
            <button
                className={styles.backToTop}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
                ↑ Back to top
            </button>
        </div>
    );
}
